import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';

/* ── Design tokens — identisch mit Landing Page ── */
const T = {
  ink:    '#111',
  muted:  '#888',
  border: '#e5e5e5',
  bg:     '#f7f7f7',
  font:   "'Inter', 'Helvetica Neue', Helvetica, Arial, sans-serif",
};

const inputStyle = {
  width: '100%',
  border: `1.5px solid ${T.border}`,
  borderRadius: 10,
  padding: '11px 14px',
  fontSize: 14,
  boxSizing: 'border-box',
  fontFamily: T.font,
  color: T.ink,
  background: 'white',
  outline: 'none',
  transition: 'border-color 0.15s',
};

const IcoCheck = () => (
  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#555" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);

export default function Register() {
  const navigate = useNavigate();

  const [form, setForm]       = useState({ fullName: '', companyName: '', email: '', password: '', passwordConfirm: '' });
  const [error, setError]     = useState('');
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    if (form.password !== form.passwordConfirm) return setError('Passwörter stimmen nicht überein.');
    if (form.password.length < 8) return setError('Passwort muss mindestens 8 Zeichen lang sein.');
    setLoading(true);

    const res = await fetch('/api/otp', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        action:      'register',
        email:       form.email,
        password:    form.password,
        fullName:    form.fullName,
        companyName: form.companyName,
      }),
    });

    const json = await res.json();
    if (!res.ok) {
      setError(json.error || 'Registrierung fehlgeschlagen.');
      setLoading(false);
      return;
    }

    // OTP skipped (domain not verified) → login directly
    if (json.skipOtp) {
      const { error: loginErr } = await supabase.auth.signInWithPassword({ email: form.email, password: form.password });
      if (loginErr) { setError(loginErr.message); setLoading(false); return; }
      navigate('/dashboard');
      return;
    }
    // Normal OTP flow
    navigate('/verify-email', { state: { email: form.email, password: form.password } });
  }

  /* ── Registrierungsformular ── */
  return (
    <div style={{ minHeight: '100vh', display: 'flex', fontFamily: T.font, background: T.bg }}>

      {/* ── Linkes Panel ── */}
      <div
        className="login-left-panel"
        style={{
          display: 'none',
          width: 460, background: T.ink, flexShrink: 0,
          flexDirection: 'column', justifyContent: 'space-between',
          padding: '52px 56px',
        }}
      >
        <div>
          <div style={{ fontSize: 15, fontWeight: 900, letterSpacing: '2.5px', textTransform: 'uppercase', color: 'white', marginBottom: 60 }}>
            IMMO<span style={{ color: T.ink }}>HQ</span>
          </div>

          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.2)',
            borderRadius: 99, padding: '4px 12px', marginBottom: 28,
          }}>
            <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#555' }} />
            <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.8px', textTransform: 'uppercase', color: 'rgba(255,255,255,0.7)' }}>
              14 Tage kostenlos
            </span>
          </div>

          <h2 style={{ fontSize: 36, fontWeight: 900, letterSpacing: '-2px', lineHeight: 1.1, color: 'white', margin: '0 0 20px' }}>
            Starte kostenlos —<br />
            <span style={{ color: '#555' }}>heute noch.</span>
          </h2>

          <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.45)', lineHeight: 1.75, margin: '0 0 44px' }}>
            Keine Kreditkarte sofort nötig. Du wählst deinen Plan nach der Registrierung.
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 13 }}>
            {[
              '14 Tage komplett kostenlos testen',
              'Kein Risiko — jederzeit kündbar',
              'DSGVO-konform · Server in Deutschland',
            ].map(item => (
              <div key={item} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{
                  width: 18, height: 18, borderRadius: '50%',
                  background: 'rgba(0,0,0,0.05)', border: '1px solid rgba(0,0,0,0.1)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  <IcoCheck />
                </div>
                <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)' }}>{item}</span>
              </div>
            ))}
          </div>
        </div>

        <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.2)', letterSpacing: '0.3px' }}>
          © {new Date().getFullYear()} ImmoHQ
        </div>
      </div>

      {/* ── Rechtes Panel — Formular ── */}
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 24px' }}>
        <div style={{ width: '100%', maxWidth: 420 }}>

          {/* Logo */}
          <div style={{ textAlign: 'center', marginBottom: 36 }}>
            <Link to="/" style={{ textDecoration: 'none' }}>
              <span style={{ fontSize: 15, fontWeight: 900, letterSpacing: '2.5px', textTransform: 'uppercase', color: T.ink }}>
                IMMO<span style={{ color: T.ink }}>HQ</span>
              </span>
            </Link>
          </div>

          {/* Karte */}
          <div style={{
            background: 'white', border: `1.5px solid ${T.border}`,
            borderRadius: 16, padding: '36px 36px 32px',
            boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
          }}>
            <h1 style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.6px', color: T.ink, margin: '0 0 6px' }}>
              Konto erstellen
            </h1>
            <p style={{ fontSize: 13, color: T.muted, margin: '0 0 28px', lineHeight: 1.5 }}>
              14 Tage kostenlos · Danach ab 29 €/Monat · Jederzeit kündbar
            </p>

            <form onSubmit={handleSubmit}>

              {/* Name */}
              <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase', color: T.muted, marginBottom: 7 }}>
                  Vollständiger Name
                </label>
                <input
                  type="text" required
                  value={form.fullName}
                  onChange={e => setForm({ ...form, fullName: e.target.value })}
                  placeholder="Max Mustermann"
                  style={inputStyle}
                  onFocus={e => e.target.style.borderColor = T.ink}
                  onBlur={e => e.target.style.borderColor = T.border}
                  autoComplete="name"
                />
              </div>

              {/* Firma */}
              <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase', color: T.muted, marginBottom: 7 }}>
                  Firmenname <span style={{ color: '#d1d5db', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(optional)</span>
                </label>
                <input
                  type="text"
                  value={form.companyName}
                  onChange={e => setForm({ ...form, companyName: e.target.value })}
                  placeholder="Mustermann Immobilien GmbH"
                  style={inputStyle}
                  onFocus={e => e.target.style.borderColor = T.ink}
                  onBlur={e => e.target.style.borderColor = T.border}
                  autoComplete="organization"
                />
              </div>

              {/* E-Mail */}
              <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase', color: T.muted, marginBottom: 7 }}>
                  E-Mail-Adresse
                </label>
                <input
                  type="email" required
                  value={form.email}
                  onChange={e => setForm({ ...form, email: e.target.value })}
                  placeholder="max@firma.de"
                  style={inputStyle}
                  onFocus={e => e.target.style.borderColor = T.ink}
                  onBlur={e => e.target.style.borderColor = T.border}
                  autoComplete="email"
                />
              </div>

              {/* Passwort */}
              <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase', color: T.muted, marginBottom: 7 }}>
                  Passwort <span style={{ color: '#d1d5db', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(mind. 8 Zeichen)</span>
                </label>
                <input
                  type="password" required minLength={8}
                  value={form.password}
                  onChange={e => setForm({ ...form, password: e.target.value })}
                  placeholder="••••••••"
                  style={inputStyle}
                  onFocus={e => e.target.style.borderColor = T.ink}
                  onBlur={e => e.target.style.borderColor = T.border}
                  autoComplete="new-password"
                />
              </div>

              {/* Passwort bestätigen */}
              <div style={{ marginBottom: 8 }}>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase', color: T.muted, marginBottom: 7 }}>
                  Passwort bestätigen
                </label>
                <input
                  type="password" required
                  value={form.passwordConfirm}
                  onChange={e => setForm({ ...form, passwordConfirm: e.target.value })}
                  placeholder="••••••••"
                  style={inputStyle}
                  onFocus={e => e.target.style.borderColor = T.ink}
                  onBlur={e => e.target.style.borderColor = T.border}
                  autoComplete="new-password"
                />
              </div>

              {error && (
                <div style={{
                  border: '1px solid #fca5a5', background: '#fef2f2',
                  borderRadius: 8, padding: '10px 14px',
                  fontSize: 12, color: '#b91c1c',
                  marginTop: 14, marginBottom: 4, lineHeight: 1.5,
                }}>
                  {error}
                </div>
              )}

              <button
                type="submit" disabled={loading}
                style={{
                  width: '100%', marginTop: 20,
                  background: loading ? T.muted : T.ink,
                  color: 'white', border: 'none',
                  borderRadius: 10, padding: '13px',
                  fontSize: 13, fontWeight: 700, letterSpacing: '0.2px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontFamily: T.font,
                  
                  transition: 'background 0.15s',
                }}
                onMouseEnter={e => { if (!loading) e.currentTarget.style.background = '#000'; }}
                onMouseLeave={e => { if (!loading) e.currentTarget.style.background = T.ink; }}
              >
                {loading ? 'Konto wird erstellt…' : 'Kostenlos starten →'}
              </button>
            </form>

            <div style={{ borderTop: `1px solid ${T.border}`, marginTop: 24, paddingTop: 20, textAlign: 'center' }}>
              <span style={{ fontSize: 13, color: T.muted }}>Bereits ein Konto? </span>
              <Link to="/login" style={{ fontSize: 13, color: T.ink, fontWeight: 700, textDecoration: 'none' }}>
                Anmelden →
              </Link>
            </div>
          </div>

          <div style={{ textAlign: 'center', marginTop: 20 }}>
            <Link to="/" style={{ fontSize: 12, color: T.muted, textDecoration: 'none' }}>
              ← Zurück zur Startseite
            </Link>
          </div>

        </div>
      </div>
    </div>
  );
}
